import { useState, useEffect, useCallback } from 'react'
import { api } from '../utils/api'
import { useAuthStore } from '../stores/authStore'
import { useToastStore } from '../stores/toastStore'

export type TrashItem =
  | { id: string; name: string; path: string; size: number; mimeType: string; deletedAt: string; type: 'file' }
  | { id: string; name: string; path: string; deletedAt: string; type: 'folder' }

// Owns trash listing data + restore/purge/empty mutations. The consuming modal
// keeps only its filter input + presentation. Behaviour mirrors the previous
// in-component logic exactly (load on open, mutate then prune local state).
export function useTrash(isOpen: boolean, onChange?: () => void) {
  const { token, csrfToken, role } = useAuthStore()
  const { addToast } = useToastStore()

  const [items, setItems] = useState<TrashItem[]>([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [confirmEmpty, setConfirmEmpty] = useState(false)

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const res = await api.listTrash(token, csrfToken || undefined)
      if (res.success && res.data) {
        const all: TrashItem[] = [
          ...res.data.files,
          ...res.data.folders,
        ].sort((a, b) => (b.deletedAt < a.deletedAt ? -1 : 1))
        setItems(all)
      } else {
        addToast({ type: 'error', message: res.error || 'Failed to load trash' })
      }
    } catch (err: any) {
      addToast({ type: 'error', message: err?.message || 'Failed to load trash' })
    } finally {
      setLoading(false)
    }
  }, [token, csrfToken, addToast])

  useEffect(() => { if (isOpen) { setConfirmEmpty(false); load() } }, [isOpen, load])

  const handleRestore = async (item: TrashItem) => {
    if (!token) return
    setBusyId(item.id)
    try {
      const res = await api.restoreTrashItem(item.id, item.type, token, csrfToken || undefined)
      if (res.success) {
        setItems(prev => prev.filter(i => i.id !== item.id))
        addToast({ type: 'success', message: `Restored ${item.name}` })
        onChange?.()
      } else {
        addToast({ type: 'error', message: res.error || 'Restore failed' })
      }
    } catch (err: any) {
      addToast({ type: 'error', message: err?.message || 'Restore failed' })
    } finally { setBusyId(null) }
  }

  const handlePurge = async (item: TrashItem) => {
    if (!token) return
    if (!window.confirm(`Permanently delete "${item.name}"? This cannot be undone.`)) return
    setBusyId(item.id)
    try {
      const res = await api.purgeTrashItem(item.id, item.type, token, csrfToken || undefined)
      if (res.success) {
        setItems(prev => prev.filter(i => i.id !== item.id))
        addToast({ type: 'success', message: `Purged ${item.name}` })
        onChange?.()
      } else {
        addToast({ type: 'error', message: res.error || 'Purge failed' })
      }
    } catch (err: any) {
      addToast({ type: 'error', message: err?.message || 'Purge failed' })
    } finally { setBusyId(null) }
  }

  const handleEmpty = async () => {
    if (!token) return
    setBusyId('__all__')
    try {
      const res = await api.emptyTrash(token, csrfToken || undefined)
      if (res.success) {
        setItems([])
        setConfirmEmpty(false)
        addToast({ type: 'success', message: 'Trash emptied' })
        onChange?.()
      } else {
        addToast({ type: 'error', message: res.error || 'Empty failed' })
      }
    } catch (err: any) {
      addToast({ type: 'error', message: err?.message || 'Empty failed' })
    } finally { setBusyId(null) }
  }

  const isAdmin = role === 'admin'

  return { items, loading, busyId, confirmEmpty, setConfirmEmpty, handleRestore, handlePurge, handleEmpty, isAdmin }
}
